import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

const imageVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const lineDraw = {
  hidden: { scaleX: 0, opacity: 0.8 },
  visible: {
    scaleX: 1,
    opacity: 1,
    transition: { duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

const leafFloat = {
  animate: {
    y: [0, -12, 0],
    rotate: [0, 4, -4, 0],
    opacity: [0.35, 0.7, 0.35],
    transition: { duration: 5, repeat: Infinity, ease: "easeInOut" },
  },
};

const LeafIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17 8C8 10 5.9 16.2 4 20c2.5-3.5 6-5.1 7-5.1 2 0 5 2.1 5 2.1s-3-5-3-9c0-2 1.5-3 1.5-3S17 6 17 8z" />
  </svg>
);

/** Images hero : visuel principal + vignette secondaire */
const HERO_IMAGE = "/photo_bg/pates.png";
const HERO_SECONDARY_IMAGE = "/photo_bg/food.png";
const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=1000&h=1000&fit=crop&q=90";

const LEAVES = [
  {
    left: "5%",
    top: "15%",
    size: "w-8 h-8",
    delay: 0,
    color: "text-emerald-500/40",
  },
  {
    left: "88%",
    top: "20%",
    size: "w-6 h-6",
    delay: 0.8,
    color: "text-green-600/50",
  },
  {
    left: "12%",
    top: "70%",
    size: "w-10 h-10",
    delay: 1.2,
    color: "text-lime-500/35",
  },
  {
    left: "92%",
    top: "75%",
    size: "w-7 h-7",
    delay: 0.4,
    color: "text-emerald-600/45",
  },
];

const HeroImage = ({ src: initialSrc = HERO_IMAGE, className = "" }) => {
  const { t } = useLanguage();
  const [src, setSrc] = React.useState(initialSrc);
  return (
    <motion.img
      src={src}
      alt={t("dish")}
      className={`w-full h-full object-cover object-center ${className}`}
      onError={() => setSrc(HERO_FALLBACK)}
      decoding="async"
    />
  );
};

/**
 * Hero modernise, visuel epure et typographie contemporaine.
 */
const HeroSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();
  return (
    <motion.section
      className="max-w-7xl mt-4 sm:mt-10 lg:mt-20 mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-12 md:py-16 lg:py-20 relative overflow-hidden w-full min-w-0 rounded-2xl sm:rounded-3xl border border-white/10 bg-black/40 shadow-[0_24px_70px_-35px_rgba(249,115,22,0.45)]"
      initial={false}
      animate="visible"
      variants={containerVariants}
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute -top-20 -left-8 h-52 w-52 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-8 h-56 w-56 rounded-full bg-amber-400/20 blur-3xl" />
        {LEAVES.map((leaf, i) => (
          <motion.div
            key={i}
            className={`absolute ${leaf.size} text-white/10`}
            style={{ left: leaf.left, top: leaf.top }}
            variants={leafFloat}
            animate="animate"
            transition={{ delay: leaf.delay }}
          >
            <LeafIcon className="w-full h-full" />
          </motion.div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-14 items-center relative z-10 px-2 sm:px-4 lg:px-6">
        <motion.div
          variants={itemVariants}
          className="order-2 lg:order-none text-center sm:text-left"
        >
          <motion.div
            className="inline-flex items-center gap-2 rounded-full border border-orange-300/35 bg-orange-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-100 mb-4"
            variants={itemVariants}
          >
            <span className="h-2 w-2 rounded-full bg-orange-300" />
            Plateforme culinaire moderne
          </motion.div>

          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl text-white leading-[0.98] mb-3 break-words font-['Caveat',_cursive]">
            <motion.span
              className="block text-white/95 font-semibold"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              {t("heroTitle")}
            </motion.span>
            <motion.span
              className="inline-block mt-1 sm:mt-2 text-4xl sm:text-6xl md:text-7xl lg:text-7xl font-bold bg-gradient-to-r from-orange-300 via-amber-300 to-orange-400 bg-clip-text text-transparent break-words"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              {t("heroTitle2")}
            </motion.span>
          </h1>
          <motion.div
            className="h-1 w-20 sm:w-36 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 origin-left mb-5 sm:mb-7 sm:mx-0 mx-auto"
            variants={lineDraw}
          />
          <motion.p
            className="text-white/85 text-sm sm:text-lg md:text-xl mb-6 sm:mb-8 max-w-xl leading-relaxed font-medium mx-auto sm:mx-0"
            variants={itemVariants}
          >
            {t("heroSubtitle")}
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center sm:justify-start"
            variants={itemVariants}
          >
            <motion.button
              onClick={onViewRecipes}
              className="group relative w-full sm:w-auto min-w-[170px] px-6 sm:px-9 py-3.5 sm:py-4 overflow-hidden rounded-2xl font-semibold tracking-[0.01em] text-white text-center text-base bg-gradient-to-r from-orange-500 to-amber-500 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 active:scale-[0.98] transition-all duration-300 touch-manipulation"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative flex items-center justify-center gap-2">
                {t("viewRecipesButton")}
                <motion.span
                  className="inline-block"
                  animate={{ x: [0, 4, 0] }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  →
                </motion.span>
              </span>
            </motion.button>
          </motion.div>
        </motion.div>
        <motion.div
          className="relative flex justify-center lg:justify-end order-first lg:order-none px-2 sm:px-0"
          variants={imageVariants}
        >
          <motion.div className="relative w-full max-w-[280px] sm:max-w-[380px] md:max-w-lg lg:max-w-xl mx-auto">
            <motion.div className="relative w-full aspect-[4/3] rounded-2xl sm:rounded-3xl overflow-hidden ring-1 ring-white/20 shadow-[0_25px_60px_-20px_rgba(120,53,15,0.65)] bg-slate-900/40 backdrop-blur">
              <HeroImage className="rounded-2xl sm:rounded-3xl" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
            </motion.div>

            <motion.div
              className="absolute -bottom-6 -left-6 w-28 h-20 sm:w-36 sm:h-24 rounded-xl overflow-hidden border border-white/30 bg-white/10 shadow-xl"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
            >
              <HeroImage src={HERO_SECONDARY_IMAGE} className="rounded-xl" />
            </motion.div>
            <motion.div
              className="absolute -top-1 -right-1 w-9 h-9 sm:w-11 sm:h-11 text-orange-300/40"
              animate={{ rotate: [0, 8, -4, 0], y: [0, -6, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <LeafIcon className="w-full h-full drop-shadow-md" />
            </motion.div>
            <motion.div
              className="absolute -bottom-1 -left-1 w-7 h-7 sm:w-9 sm:h-9 text-amber-300/40"
              animate={{ rotate: [0, -6, 4, 0], y: [0, 5, 0] }}
              transition={{
                duration: 4.5,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5,
              }}
            >
              <LeafIcon className="w-full h-full drop-shadow-md" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HeroSection;
